import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { X, Plus, Tag, Users, Star, Heart, Briefcase, Globe, Phone, Mail } from "lucide-react";
import { trimPhoneNumber } from "@/utils/phoneNumberFormatter";

interface MetadataEntry {
  id: string;
  key: string;
  value: string;
  type: string;
}

interface DynamicMetadataFieldProps {
  initialMetadata?: Record<string, any>;
  onMetadataChange: (metadata: Record<string, any>) => void;
}

const METADATA_TYPES = [
  { key: "tags", label: "Tags", icon: Tag, type: "tags" },
  { key: "source", label: "Source", icon: Globe, type: "text" },
  { key: "priority", label: "Priority", icon: Star, type: "select" },
  { key: "interests", label: "Interests", icon: Heart, type: "tags" },
  { key: "role_level", label: "Role Level", icon: Briefcase, type: "select" },
  { key: "contact_preference", label: "Contact Preference", icon: Phone, type: "select" },
  { key: "custom", label: "Custom Field", icon: Tag, type: "text" },
];

export const DynamicMetadataField = ({ initialMetadata = {}, onMetadataChange }: DynamicMetadataFieldProps) => {
  const isInitialized = useRef(false);
  const [metadataEntries, setMetadataEntries] = useState<MetadataEntry[]>([]);

  // Initialize metadata entries from initialMetadata
  useEffect(() => {
    // Only reset if we haven't initialized yet or if we have meaningful initial data
    if (isInitialized.current && Object.keys(initialMetadata).length === 0) {
      return;
    }
    
    // Only update if we don't have any entries yet or if initial metadata actually changed
    if (metadataEntries.length === 0 && Object.keys(initialMetadata).length > 0) {
      const entries = Object.entries(initialMetadata)
        .filter(([_, value]) => value !== undefined && value !== null && value !== "")
        .map(([key, value], index) => {
          const metadataType = METADATA_TYPES.find(t => t.key === key);
          return {
            id: `metadata-${index}`,
            key,
            value: Array.isArray(value) ? value.join(", ") : String(value),
            type: metadataType?.type || "text",
          };
        });
      
      setMetadataEntries(entries);
    }
    
    isInitialized.current = true;
  }, [initialMetadata, metadataEntries.length]);

  const addMetadataEntry = () => {
    const newEntry: MetadataEntry = {
      id: `metadata-${Date.now()}`,
      key: "tags",
      value: "",
      type: "tags",
    };
    setMetadataEntries([...metadataEntries, newEntry]);
  };

  const updateFormValues = (entries: MetadataEntry[]) => {
    const metadataObject: Record<string, any> = {};
    // Only send non-empty entries to parent form, but keep all entries in UI state
    entries.forEach((entry) => {
      if (entry.value.trim()) {
        let processedValue: any = entry.value;
        
        // Process based on field type
        if (entry.type === "tags" && entry.value.includes(",")) {
          processedValue = entry.value.split(",").map(tag => tag.trim()).filter(tag => tag);
        }
        
        metadataObject[entry.key] = processedValue;
      }
    });
    console.log("🔄 DynamicMetadataField: Updating metadata:", metadataObject);
    onMetadataChange(metadataObject);
  };

  const removeMetadataEntry = (id: string) => {
    const updatedEntries = metadataEntries.filter((entry) => entry.id !== id);
    setMetadataEntries(updatedEntries);
    updateFormValues(updatedEntries);
  };

  const updateMetadataEntry = (id: string, field: keyof MetadataEntry, value: string) => {
    const updatedEntries = metadataEntries.map((entry) => {
      if (entry.id === id) {
        const updatedEntry = { ...entry, [field]: value };
        
        // Update type when key changes and clear value if type changes
        if (field === "key") {
          const metadataType = METADATA_TYPES.find(t => t.key === value);
          const newType = metadataType?.type || "text";
          updatedEntry.type = newType;
          // Clear value when changing field type to avoid incompatible data
          if (newType !== entry.type) {
            updatedEntry.value = "";
          }
        }
        
        return updatedEntry;
      }
      return entry;
    });
    
    // Always update UI state immediately to prevent disappearing fields
    setMetadataEntries(updatedEntries);
    // Only update form values (which filters empty values) 
    updateFormValues(updatedEntries);
  };

  const getIcon = (key: string) => {
    const metadataType = METADATA_TYPES.find(t => t.key === key);
    const IconComponent = metadataType?.icon || Tag;
    return <IconComponent className="h-4 w-4" />;
  };

  const renderValueInput = (entry: MetadataEntry) => {
    switch (entry.type) {
      case "select":
        if (entry.key === "priority") {
          return (
            <Select
              value={entry.value}
              onValueChange={(value) => updateMetadataEntry(entry.id, "value", value)}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          );
        } else if (entry.key === "role_level") {
          return (
            <Select
              value={entry.value}
              onValueChange={(value) => updateMetadataEntry(entry.id, "value", value)}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select role level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual_contributor">Individual Contributor</SelectItem>
                <SelectItem value="team_lead">Team Lead</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="vp">VP</SelectItem>
                <SelectItem value="c_level">C-Level</SelectItem>
              </SelectContent>
            </Select>
          );
        } else if (entry.key === "contact_preference") {
          return (
            <Select
              value={entry.value}
              onValueChange={(value) => updateMetadataEntry(entry.id, "value", value)}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="in_person">In Person</SelectItem>
              </SelectContent>
            </Select>
          );
        }
        return (
          <Input
            placeholder="Enter value"
            value={entry.value}
            onChange={(e) => updateMetadataEntry(entry.id, "value", e.target.value)}
            className="h-8"
          />
        );
      
      case "textarea":
        return (
          <Textarea
            placeholder="Enter detailed information"
            value={entry.value}
            onChange={(e) => updateMetadataEntry(entry.id, "value", e.target.value)}
            className="min-h-[60px]"
          />
        );
      
      case "tags":
        return (
          <Input
            placeholder="Enter tags separated by commas"
            value={entry.value}
            onChange={(e) => updateMetadataEntry(entry.id, "value", e.target.value)}
            className="h-8"
          />
        );
      
      default:
        // Check if this is a phone field and apply trimming
        const isPhoneField = entry.key === 'phone';
        
        return (
          <Input
            placeholder="Enter value"
            value={entry.value}
            onChange={(e) => {
              const value = isPhoneField ? trimPhoneNumber(e.target.value) : e.target.value;
              updateMetadataEntry(entry.id, "value", value);
            }}
            onBlur={(e) => {
              if (isPhoneField) {
                const trimmedValue = trimPhoneNumber(e.target.value);
                updateMetadataEntry(entry.id, "value", trimmedValue);
              }
            }}
            className="h-8"
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      {metadataEntries.map((entry) => (
        <div key={entry.id} className="flex items-center gap-2">
          <div className="w-32">
            <Select
              value={entry.key}
              onValueChange={(value) => updateMetadataEntry(entry.id, "key", value)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue 
                  placeholder="Select type"
                  className="text-xs"
                >
                  <div className="flex items-center space-x-1">
                    {getIcon(entry.key)}
                    <span className="truncate">{METADATA_TYPES.find(t => t.key === entry.key)?.label || entry.key}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {METADATA_TYPES.map((type) => (
                  <SelectItem key={type.key} value={type.key}>
                    <div className="flex items-center space-x-2">
                      <type.icon className="h-4 w-4" />
                      <span>{type.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-1">
            {renderValueInput(entry)}
          </div>
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeMetadataEntry(entry.id)}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addMetadataEntry}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Metadata Field
      </Button>
    </div>
  );
};

export default DynamicMetadataField;